import * as Sentry from '@sentry/browser';
import { AnalyticsService } from './analytics.service';
import { buildConversationContext, parseConversationCookies } from './conversation-context';
import { connectionDroppedAround, noteRequestWithoutAnswer } from './connection-drop';
import { pageDepartingSince } from './page-leaving';

export type RateFinderMode = 'form' | 'assistant';

export interface RateFinderSentryContext {
  rate_finder_session_id?: string;
  thread_id?: string;
  lead_id?: string;
  experiment?: string;
  arm?: string;
  mode?: RateFinderMode;
  surface?: string;
  domain?: string;
  page?: string;
  step?: string;
  device_class?: string;
  build?: string;
}

const CONTEXT_KEYS: Array<keyof RateFinderSentryContext> = [
  'rate_finder_session_id', 'thread_id', 'lead_id', 'experiment', 'arm', 'mode',
  'surface', 'domain', 'page', 'step', 'device_class', 'build',
];

const BREADCRUMB_KEYS = [
  'api', 'from_step', 'http_status', 'mode', 'outcome', 'reason', 'source',
  'status', 'step', 'surface', 'to_step',
  // FRU-24080: a step post sent again after no answer (the action name, which try, and how many before a report).
  'action', 'attempt', 'retries',
];

let currentContext: RateFinderSentryContext = {};
let active = false;
let latestBreadcrumb: any = null;
let latestException: any = null;
// FRU-23258: marks an event whose rate_finder context was pinned when its request failed, so annotation keeps it.
const PINNED_CONTEXT_TAG = 'rate_finder_context';

function containsPii(value: string): boolean {
  const streetAddress = /\b\d{1,6}\s+(?:[A-Z0-9]+\s+){0,4}(?:ST(?:REET)?|RD|ROAD|AVE(?:NUE)?|BLVD|BOULEVARD|DR(?:IVE)?|LN|LANE|CT|COURT|WAY)\b/i;
  return /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i.test(value)
    || /(?:^|\D)(?:\+?\d[\s().-]*){10,15}(?!\d)/.test(value)
    || streetAddress.test(value);
}

function bounded(value: any, limit: number = 128): string {
  if (value === null || value === undefined || Array.isArray(value) || typeof value === 'object') { return ''; }
  const normalized = String(value).replace(/[\x00-\x1F\x7F]/g, '').trim().slice(0, limit);
  return normalized && !containsPii(normalized) ? normalized : '';
}

function deviceClass(userAgent: string): string {
  if (/bot|crawler|spider|headless/i.test(userAgent)) { return 'bot'; }
  if (/ipad|tablet|kindle/i.test(userAgent)) { return 'tablet'; }
  if (/mobile|iphone|android/i.test(userAgent)) { return 'mobile'; }
  return 'desktop';
}

function buildIdentifier(): string {
  try {
    const w: any = window;
    const globalBuild = bounded(w.__FRU_BUILD_SHA || w.__FRU_RELEASE__, 64);
    if (globalBuild) { return globalBuild; }
    const meta = document.querySelector('meta[name="fru-build-sha"], meta[name="fru-release"]');
    return bounded(meta && meta.getAttribute('content'), 64);
  } catch (e) {
    return '';
  }
}

/** Return the existing session identifier or mint the same one used by chat/action API context. */
export function ensureRateFinderSessionId(): string {
  try {
    const cookies = parseConversationCookies(document.cookie || '');
    if (Object.prototype.hasOwnProperty.call(cookies, 'fruRemoveCookies')) { return ''; }
    if (cookies['rate_finder_session_id']) { return cookies['rate_finder_session_id']; }

    const browserCrypto: any = (window as any).crypto;
    const sessionId = (browserCrypto && browserCrypto.randomUUID)
      ? browserCrypto.randomUUID()
      : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
          const n = Math.floor(Math.random() * 16);
          return (ch === 'x' ? n : ((n & 0x3) | 0x8)).toString(16);
        });
    const expires = new Date(Date.now() + 86400000).toUTCString();
    const host = window.location.hostname.split('.');
    const domain = host.length > 1 ? '; domain=.' + host.slice(-2).join('.') : '';
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = 'rate_finder_session_id=' + encodeURIComponent(sessionId)
      + '; expires=' + expires + '; path=/' + domain + '; SameSite=Lax' + secure;
    return sessionId;
  } catch (e) {
    return '';
  }
}

function browserConversationContext(): any {
  try {
    return buildConversationContext({
      href: window.location.href,
      referrer: document.referrer || '',
      cookie: document.cookie || '',
      rateFinderSessionId: ensureRateFinderSessionId(),
      hopeMarkers: AnalyticsService.parseHopeGa4Markers(window.location.search, document.cookie || ''),
    });
  } catch (e) {
    return {};
  }
}

function isRateFinderLocation(): boolean {
  try {
    const host = window.location.hostname.toLowerCase();
    return host === 'lowratefinder.ai' || host.endsWith('.lowratefinder.ai')
      || host === 'airatefinder.com' || host.endsWith('.airatefinder.com')
      || window.location.pathname.toLowerCase().indexOf('/rates-form') >= 0
      || /[?&]startAssistant=(?:0|1|yes|true)(?:&|$)/i.test(window.location.search);
  } catch (e) {
    return false;
  }
}

/** Prime the current Rate Finder scope without accepting form values or free-form text. */
export function primeRateFinderSentryContext(
  mode: RateFinderMode,
  force: boolean = false,
  conversationContext?: any,
  step?: string,
): void {
  if (!force && !isRateFinderLocation()) { return; }
  const context = conversationContext && typeof conversationContext === 'object'
    ? conversationContext
    : browserConversationContext();
  active = true;
  currentContext = {};
  latestBreadcrumb = null;
  latestException = null;
  updateRateFinderSentryContext({
    rate_finder_session_id: context.rate_finder_session_id,
    experiment: context.experiment_name || context.experiment_id,
    arm: context.experiment_arm || context.variation,
    mode,
    surface: context.ai_surface,
    domain: context.domain,
    page: context.page_path,
    step,
    device_class: (() => { try { return deviceClass(navigator.userAgent || ''); } catch (e) { return ''; } })(),
    build: buildIdentifier(),
  });
}

export function updateRateFinderSentryContext(patch: RateFinderSentryContext): void {
  if (!active || !patch || typeof patch !== 'object') { return; }
  CONTEXT_KEYS.forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(patch, key)) { return; }
    const value = bounded(patch[key], key === 'page' ? 255 : 128);
    if (value) { (currentContext as any)[key] = value; }
    else { delete (currentContext as any)[key]; }
  });
}

/** Add the allowlisted Rate Finder scope to every Sentry event emitted while the flow is active. */
export function annotateRateFinderSentryEvent(event: any): any {
  if (!active || !event) { return event; }
  // FRU-23258: a report sent after a delay keeps the context it had when the request failed, not the flow's current step
  // and thread.
  const pinned = event.tags && event.tags[PINNED_CONTEXT_TAG] === 'failure_time'
    && event.contexts && event.contexts.rate_finder && typeof event.contexts.rate_finder === 'object'
    ? event.contexts.rate_finder : null;
  const source: RateFinderSentryContext = pinned || currentContext;
  const tags: any = {
    fru_ticket: 'FRU-21436',
    instrumentation_ticket: 'FRU-21495',
    feature: 'rate_finder',
  };
  CONTEXT_KEYS.forEach((key) => {
    const value = source[key];
    if (value) { tags[key] = value; }
  });
  event.tags = { ...(event.tags || {}), ...tags };
  event.contexts = {
    ...(event.contexts || {}),
    rate_finder: { ...source },
  };
  return event;
}

function breadcrumbData(data: any): any {
  const clean: any = {};
  if (!data || typeof data !== 'object' || Array.isArray(data)) { return clean; }
  BREADCRUMB_KEYS.forEach((key) => {
    const value = bounded(data[key]);
    if (value) { clean[key] = value; }
  });
  return clean;
}

export function addRateFinderBreadcrumb(action: string, data: any = {}, level: 'info' | 'warning' | 'error' = 'info'): void {
  if (!active) { return; }
  const message = bounded(action, 64);
  if (!message) { return; }
  latestBreadcrumb = {
    category: 'rate_finder.lifecycle',
    message,
    level,
    data: breadcrumbData(data),
  };
  try {
    Sentry.addBreadcrumb(latestBreadcrumb);
  } catch (e) { /* telemetry must never affect the consumer flow */ }
}

/**
 * FRU-24164: how Sentry groups a failure that isn't an Error of our code (a failed request): by the action and how it failed
 * (no answer, 4xx, 5xx). Without it Sentry grouped it by the stack of the reporting call, whose minified names change between
 * some builds, so one step-post failure had opened seven issues in a week (ANGULAR-77A, 7EE, 7EJ, 7ER, 7EA, 77K, 7F6).
 * Real exceptions keep Sentry's own grouping by their stack (null here).
 */
export function rateFinderFailureFingerprint(error: any, action: string): string[] | null {
  if (error instanceof Error) { return null; }
  const status = error && typeof error.status === 'number' ? error.status : null;
  const how = status === null ? 'no-status' : status === 0 ? 'no-answer' : Math.floor(status / 100) + 'xx';
  return ['rate-finder-request', action, how];
}

export function captureRateFinderException(
  error: any,
  action: string,
  data: any = {},
  failureContext: RateFinderSentryContext | null = null,
): void {
  if (!active) { return; }
  const safeAction = bounded(action, 64) || 'failure';
  const cleanData = breadcrumbData(data);
  const fingerprint = rateFinderFailureFingerprint(error, safeAction);
  latestException = { action: safeAction, data: cleanData, context: { ...(failureContext || currentContext) }, fingerprint };
  addRateFinderBreadcrumb(safeAction, cleanData, 'error');
  try {
    const exception = error instanceof Error ? error : new Error('Rate Finder ' + safeAction);
    const captureContext: any = { tags: { action: safeAction }, extra: cleanData };
    if (fingerprint) { captureContext.fingerprint = fingerprint; }
    if (failureContext) {
      captureContext.tags[PINNED_CONTEXT_TAG] = 'failure_time';
      captureContext.contexts = { rate_finder: { ...failureContext } };
    }
    Sentry.captureException(exception, captureContext);
  } catch (e) { /* telemetry must never affect the consumer flow */ }
}

// FRU-23258: how far before a status-0 failure a page-leaving signal still explains it, and how long to wait before
// deciding the visitor stayed. A cancelled request's error can arrive before the page's own pagehide, so the decision
// waits; if the page is gone by then, the timer never runs and nothing is reported. A browser Back alone does not count:
// inside Hope it moves to the previous question and the page stays, and a Back that does leave fires pagehide.
export const REQUEST_FAILURE_LEAVE_WINDOW_MS = 1000;
export const REQUEST_FAILURE_RECHECK_MS = 1500;

/**
 * Report a failed API request. A real HTTP error is reported at once. A status 0 (the request never got an answer) is
 * reported only if the visitor is still on a visible page once the recheck has passed; one caused by the visitor leaving
 * (hidden page, pagehide, freeze, or the leave prompt) or by the phone dropping its connection becomes a breadcrumb instead.
 */
export function captureRateFinderRequestFailure(error: any, action: string, data: any = {}): void {
  const status = error && typeof error.status === 'number' ? error.status : null;
  if (status !== 0) {
    captureRateFinderException(error, action, data);
    return;
  }
  const failedAt = Date.now();
  const since = failedAt - REQUEST_FAILURE_LEAVE_WINDOW_MS;
  const failureOrder = noteRequestWithoutAnswer(failedAt);
  // The step and thread can change during the wait; the report describes where the request failed.
  const failureContext: RateFinderSentryContext = { ...currentContext };
  const decide = () => {
    const reason = pageDepartingSince(since);
    if (reason) {
      addRateFinderBreadcrumb('request_cancelled_leaving', { ...data, reason, outcome: bounded(action, 64) });
      return;
    }
    // FRU-23847: the phone dropped its connection (offline, a network switch, or other requests failing with it).
    const drop = connectionDroppedAround(failedAt, REQUEST_FAILURE_LEAVE_WINDOW_MS, undefined, failureOrder);
    if (drop) {
      addRateFinderBreadcrumb('request_failed_connection', { ...data, reason: drop, outcome: bounded(action, 64) });
      return;
    }
    captureRateFinderException(error, action, data, failureContext);
  };
  if (pageDepartingSince(since)) { decide(); return; }
  try { setTimeout(decide, REQUEST_FAILURE_RECHECK_MS); } catch (e) { decide(); }
}

/** Test-only snapshot; production callers cannot mutate the live context. */
export function rateFinderSentryContextSnapshot(): RateFinderSentryContext {
  return { ...currentContext };
}

/** Test-only snapshot; production callers cannot mutate the recorded breadcrumb. */
export function rateFinderBreadcrumbSnapshot(): any {
  return latestBreadcrumb ? { ...latestBreadcrumb, data: { ...latestBreadcrumb.data } } : null;
}

/** Test-only snapshot; production callers cannot mutate the recorded exception. */
export function rateFinderExceptionSnapshot(): any {
  return latestException ? { ...latestException, data: { ...latestException.data } } : null;
}
